import { createSlice } from "@reduxjs/toolkit";

import {
  ChartActivityInfo,
  DashboardLocIdInfo,
  GeoLocaDashboard,
} from "../../actions/dashboard/dashboardTypes";
import { getPocketLabRecentVersion } from "../../actions/dashboard/dashboardAsyncActions";
import { logOut } from "../layout";
import {
  fetchCerealSlugs,
  fetchCropInfo,
} from "../../actions/dashboard/dynamicCharts";
import {
  activityChartValues,
  fetchUserActivity,
} from "../../actions/dashboard/dashboardDataAsync.actions";
import { InarixGridFilterModel } from "../dataTable/data";

export interface CerealSlugs {
  slug: string;
  translation: string;
  id: number;
}

export interface DashboardState {
  loadingDashboardInfo: boolean;
  dateRangePickerDashboard: boolean;
  isComingFromMap: boolean;
  geoLocInfo: GeoLocaDashboard;
  error: string;
  geoLocNameSelected: string;
  chartActivity: ChartActivityInfo[];
  chartActivity2: any[];

  allLocations: boolean;
  locationsSelected: DashboardLocIdInfo[];
  pocketlabVersion: string;

  pocketLabChart: any[];
  rawRowsActivityTable: any[];
  countOfUsers: {
    countOfUsersActive: number;
    countOfUsersInactive: number;
    totalUsers: number;
  };
  rawRowsPocketLabTable: any[];
  comesFromMap: boolean;
  cropsSelected: CerealSlugs[];

  /// new dynamic charts state
  loading: {
    slugs: boolean;
    dynamicCharts: boolean;
  };
  errors: {
    slugs: string;
    dynamicCharts: string;
  };
  selectedCrop: {
    slug: string;
    translation: string;
  };

  dynamicCharts: {
    createArrayForCharts: { variety: string; actions: number }[];
    sumActions: number;
    CreateArrayAverageForAvgChart: { variety: string; average: number }[];
  };

  activityChart: {
    data: any[];
    loading: boolean;
    error: string;
  };

  activityData: {
    data: Array<{
      countInfo: any[];
      cerealType: {
        id: number;
        name: string;
        color: string;
      };
      data: Array<{
        countInfo: number;
        date: string;
        rawDate: string;
      }>;
    }>;
    loading: boolean;
    error: string;
  };

  allDataResults: {
    data: Array<{
      specie: string;
      countInfo: { date: string; activity: string }[];
    }>;
  };

  usersTable: Array<{
    user: string;
    site: string;
    totalActions: number;
    varietyActions: Array<{
      cereal: string;
      count: number;
    }>;
  }>;

  hasDataInCharts: {
    hasData: boolean;
    hasDataBeenLoaded: boolean;
  };

  loadingProgress: {
    isLoading: boolean;
    loadedRecords: number;
    totalRecords: number;
    percentage: number;
  };

  totalActions: number;

  //custom filter component
  filterModel: InarixGridFilterModel;
  showFilterForm: boolean;
  preselectedFilterField: string | null;
  loadFilterForFirstTime: boolean;
}

export const initialState: DashboardState = {
  loadingDashboardInfo: false,
  dateRangePickerDashboard: false,
  pocketlabVersion: "",
  isComingFromMap: false,
  geoLocInfo: {
    data: {
      total: 0,
      data: [{ id: 0, activity: {} }],
    },
  } as GeoLocaDashboard,
  error: "",
  geoLocNameSelected: "",
  chartActivity: [],
  chartActivity2: [],

  allLocations: false,
  locationsSelected: [] as DashboardLocIdInfo[],

  pocketLabChart: [["0.0.0", 1]],
  rawRowsActivityTable: [],
  rawRowsPocketLabTable: [],
  countOfUsers: {
    countOfUsersActive: 0,
    countOfUsersInactive: 0,
    totalUsers: 0,
  },

  comesFromMap: false,
  cropsSelected: [] as CerealSlugs[],
  loading: {
    slugs: false,
    dynamicCharts: false,
  },
  errors: {
    slugs: "",
    dynamicCharts: "",
  },
  selectedCrop: {
    slug: "",
    translation: "",
  },

  dynamicCharts: {
    createArrayForCharts: [{ variety: "", actions: 0 }],
    sumActions: 0,
    CreateArrayAverageForAvgChart: [{ variety: "", average: 0 }],
  },

  activityChart: {
    data: [],
    loading: false,
    error: "",
  },

  activityData: {
    data: [],
    loading: false,
    error: "",
  },

  allDataResults: {
    data: [
      {
        specie: "",
        countInfo: [{ date: "", activity: "" }],
      },
    ],
  },
  usersTable: [],

  hasDataInCharts: {
    hasData: false,
    hasDataBeenLoaded: false,
  },

  loadingProgress: {
    isLoading: false,
    loadedRecords: 0,
    totalRecords: 0,
    percentage: 0,
  },

  totalActions: 0,

  //custom filter component
  filterModel: { items: [] },
  showFilterForm: false,
  preselectedFilterField: null,
  loadFilterForFirstTime: true,
};

export const DashboardSlice = createSlice({
  name: "DashboardReducer",
  initialState,
  reducers: {
    //new custom filter component
    setLoadForFirstTime(state, action) {
      return {
        ...state,
        loadFilterForFirstTime: action.payload,
      };
    },
    setFilterModel(state, action) {
      return {
        ...state,
        filterModel: action.payload,
      };
    },

    setShowFilterForm(state, action) {
      return {
        ...state,
        showFilterForm: action.payload,
      };
    },

    setPreselectedFilterField(state, action) {
      return {
        ...state,
        preselectedFilterField: action.payload,
      };
    },

    resetUsersTable(state) {
      state.usersTable = initialState.usersTable;
    },
    setHasDataInCharts(state, action) {
      state.hasDataInCharts = action.payload;
    },

    setLoadingProgress(state, action) {
      state.loadingProgress.loadedRecords = action.payload.loaded;
      state.loadingProgress.totalRecords = action.payload.total;
      state.loadingProgress.percentage = Math.round(
        (action.payload.loaded / action.payload.total) * 100,
      );
      state.loadingProgress.isLoading =
        action.payload.loaded < action.payload.total;
    },

    resetLoadingProgress(state) {
      state.loadingProgress = initialState.loadingProgress;
    },

    startLoading(state, action) {
      state.loadingProgress.isLoading = true;
      state.loadingProgress.totalRecords = action.payload;
      state.loadingProgress.loadedRecords = 0;
      state.loadingProgress.percentage = 0;
    },

    setTotalActions(state, action) {
      state.totalActions = action.payload;
    },

    resetAll(state) {
      return {
        ...state,
        usersTable: initialState.usersTable,
        allDataResults: initialState.allDataResults,
        chartActivity: initialState.chartActivity,
        countOfUsers: initialState.countOfUsers,
        pocketLabChart: initialState.pocketLabChart,
        rawRowsPocketLabTable: initialState.rawRowsPocketLabTable,
        dynamicCharts: initialState.dynamicCharts,
        selectedCrop: initialState.selectedCrop,
        comesFromMap: initialState.comesFromMap,
      };
    },
    setUsersTable(state, action) {
      return {
        ...state,
        usersTable: action.payload,
      };
    },

    setCrop(state, action) {
      return {
        ...state,
        selectedCrop: action.payload,
      };
    },
    setComesFromMap(state, action) {
      return {
        ...state,
        comesFromMap: action.payload,
      };
    },
    setPocketLabChartData(state, action) {
      return {
        ...state,
        pocketLabChart: action.payload,
      };
    },
    setRowsPocketlabTable(state, action) {
      return {
        ...state,
        rawRowsPocketLabTable: action.payload,
      };
    },

    setVisibleRangeDashboardPicker(state, action) {
      return {
        ...state,
        dateRangePickerDashboard: action.payload,
      };
    },

    /**
     * @Charts
     */
    setCounOfUserActivityTab(state, action) {
      return {
        ...state,
        countOfUsers: action.payload,
      };
    },

    setLocationNameDashboardInput(state, action) {
      return {
        ...state,
        geoLocNameSelected: action.payload,
      };
    },

    setAllSilos(state, action) {
      return {
        ...state,
        allLocations: action.payload,
      };
    },

    resetChartAcivity(state) {
      return {
        ...state,
        chartActivity: initialState.chartActivity,
        countOfUsers: initialState.countOfUsers,
        pocketLabChart: initialState.pocketLabChart,
      };
    },

    logoutDashboard() {
      return initialState;
    },

    /**
     * @Charts
     */
  },

  extraReducers(builder) {
    builder
      .addCase(fetchCropInfo.pending, (state) => {
        state.loading.dynamicCharts = true;
        state.dynamicCharts = initialState.dynamicCharts;
        state.errors.dynamicCharts = "";
      })
      .addCase(fetchCropInfo.fulfilled, (state, action) => {
        state.loading.dynamicCharts = false;
        state.dynamicCharts = action.payload as DashboardState["dynamicCharts"];
      })
      .addCase(fetchCropInfo.rejected, (state, action) => {
        state.loading.dynamicCharts = false;
        state.errors.dynamicCharts = action.payload as string;
      });

    builder
      .addCase(fetchCerealSlugs.pending, (state) => {
        state.loading.slugs = true;
        state.errors.slugs = "";
      })
      .addCase(fetchCerealSlugs.fulfilled, (state, action) => {
        state.loading.slugs = false;
        state.cropsSelected = action.payload;
      })
      .addCase(fetchCerealSlugs.rejected, (state, action) => {
        state.loading.slugs = false;
        state.errors.slugs = action.payload as string;
      });

    builder
      .addCase(getPocketLabRecentVersion.pending, (state) => {
        state.loadingDashboardInfo = true;
        state.error = "";
      })
      .addCase(getPocketLabRecentVersion.fulfilled, (state, action) => {
        state.loadingDashboardInfo = false;
        state.pocketlabVersion = action.payload;
      })
      .addCase(getPocketLabRecentVersion.rejected, (state, action) => {
        state.loadingDashboardInfo = false;
        state.error = action.payload as string;
      });

    builder.addCase(activityChartValues.pending, (state) => {
      state.activityChart.loading = true;
      state.activityChart.error = "";
    });
    builder.addCase(activityChartValues.fulfilled, (state, action) => {
      state.activityChart.loading = false;
      state.allDataResults = action.payload as { data: [] };
    });
    builder.addCase(activityChartValues.rejected, (state, action) => {
      state.activityChart.loading = false;
      state.allDataResults = initialState.allDataResults;
      state.activityChart.error = action.payload as string;
    });

    builder.addCase(fetchUserActivity.pending, (state) => {
      state.activityData.loading = true;
      state.activityData.error = "";
    });
    builder.addCase(fetchUserActivity.fulfilled, (state, action) => {
      state.activityData.loading = false;
      state.activityData.data = action.payload as any;
    });
    builder.addCase(fetchUserActivity.rejected, (state, action) => {
      state.activityData.loading = false;
      state.activityData.data = initialState.activityData.data;
      state.activityData.error = action.payload as string;
    });

    builder.addCase(logOut, () => {
      return initialState;
    });
  },
});
export const {
  //new custom filter component
  setFilterModel,
  setShowFilterForm,
  setPreselectedFilterField,
  setLoadForFirstTime,
  //new custom filter component
  resetUsersTable,
  setHasDataInCharts,
  setLoadingProgress,
  resetLoadingProgress,
  startLoading,
  setTotalActions,
  resetAll,
  setCounOfUserActivityTab,
  setComesFromMap,
  setPocketLabChartData,
  setRowsPocketlabTable,
  setVisibleRangeDashboardPicker,
  setLocationNameDashboardInput,
  resetChartAcivity,

  setAllSilos,
  logoutDashboard,
  setCrop,
  setUsersTable,
} = DashboardSlice.actions;
export const Dashboard = DashboardSlice.reducer;
